'use strict';

import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useFetch } from '../../../app/composables/fetch';
import { camelCase, snakeCase } from '../../../app/utils/change-case';

/**
 * Pinia store for a single standing
 */
export const useStandingStore = defineStore('standing', () => {
  /**
   * Current page
   */
  const page = ref(1);

  /**
   * Current standing
   */
  const standing = ref(null);

  /**
   * All Users
   */
  const standings = ref([]);

  /**
   * Loading
   */
  const loading = ref(false);

  /**
   * Submitting
   */
  const submitting = ref(false);

  /**
   * Error
   */
  const error = ref(null);

  /**
   * Get standing by id
   * @param {Number} id - id of standing
   * @returns {Void}
   */
  const getById = async (standingId) => {
    // Clear standing
    standing.value = null;

    // Reset error
    error.value = null;

    // Call api
    loading.value = true;
    const { data, error: fetchError } = await useFetch(`standings/${standingId}`)
      .get()
      .json();
    loading.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      return;
    }

    // Change keys to camel case
    standing.value = camelCase(data.value.data);
  };

  /**
   * Get all standings
   * @returns {Promise} standings - array of standing objects
   */
  const getAll = async () => {
    // Clear standings
    standings.value = [];

    // Reset page
    page.value = 1;

    // Reset error
    error.value = null;

    // Call api
    loading.value = true;
    const { data, error: fetchError } = await useFetch('standings').get().json();
    loading.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      return;
    }

    // Change keys to camel case
    standings.value = camelCase(data.value.data);
  };

  /**
   * Add standing
   * @param {Obejct} standing - standing object
   * @returns {Promise} standing - simple object of standing
   */
  const add = async () => {
    // Reset error
    error.value = null;

    // Change keys to snake case
    const payload = snakeCase(standing.value);

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch('standings')
      .post(payload)
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      //throw error.value;
      return;
    }

    // Change keys to camel case
    standing.value = camelCase(data.value.data);

    // Add standing from response to list
    standings.value.push(standing.value);

    return Promise.resolve(standing.value);
  };

  /**
   * Edit standing
   * @param {Obejct} standing - standing object
   * @returns {Promise} standing - simple object of standing
   */
  const edit = async () => {
    // Reset error
    error.value = null;

    // Change keys to snake case
    const payload = snakeCase(standing.value);

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch(
      `standings/${standing.value.id}`,
    )
      .patch(payload)
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      //throw error.value;
      return;
    }

    // Change keys to camel case
    standing.value = camelCase(data.value.data);

    return Promise.resolve(standing.value);
  };

  /**
   * Delete standing
   * @param {Number} id - id of standing
   * @returns {Promise} standing - simple object of standing
   */
  const _delete = async (standingId) => {
    // Reset error
    error.value = null;

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch(`standings/${standingId}`)
      .delete()
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      //throw error.value;
      return;
    }

    // Remove standing from list
    standings.value = standings.value.filter((standing) => standing.id !== standingId);

    return Promise.resolve(true);
  };

  return {
    standing,
    standings,
    error,
    loading,
    submitting,
    getById,
    getAll,
    add,
    edit,
    _delete,
  };
});
